import { AuthKey } from '../../../lib/gramjs/crypto/AuthKey';
import { CTR } from '../../../lib/gramjs/crypto/CTR';
import { Logger } from '../../../lib/gramjs/extensions';
import {
  convertToLittle, getByteArray, modExp, readBigIntFromBuffer, sha1, sha256,
} from '../../../lib/gramjs/Helpers';
import MTProtoState from '../../../lib/gramjs/network/MTProtoState';

type DhConfig = {
  p: number[];
  g: number;
  random: number[];
};

export type PhoneCallStateSnapshot = {
  isOutgoing: boolean;
  pHex?: string;
  randomHex?: string;
  gAHex?: string;
  gBHex?: string;
};

let currentPhoneCallState: PhoneCallState | undefined;

// tgcalls-compatible AES-256-CTR key derivation (x=0 for caller, x=8 for callee)
// This matches EncryptedConnection.cpp in tgcalls, NOT MTProtoState which uses x=128+offset
async function callCryptoKeyIv(authKey: Buffer, msgKey: Buffer, x: number) {
  const [sha256a, sha256b] = await Promise.all([
    sha256(Buffer.concat([msgKey, authKey.slice(x, x + 36)])),
    sha256(Buffer.concat([authKey.slice(x + 40, x + 76), msgKey])),
  ]);
  const key = Buffer.concat([sha256a.slice(0, 8), sha256b.slice(8, 24), sha256a.slice(24, 32)]);
  const iv = Buffer.concat([sha256b.slice(0, 4), sha256a.slice(8, 16), sha256b.slice(24, 28)]);
  return { key, iv };
}

class PhoneCallState {
  private state?: MTProtoState;

  private seq = 0;

  private decodeSeq = 0;

  private gA?: bigint;

  private gB?: bigint;

  private p?: bigint;

  private random?: bigint;

  private authKeyRaw?: Buffer;

  private waitForState: Promise<void>;

  private resolveState?: VoidFunction;

  constructor(
    private isOutgoing: boolean,
  ) {
    this.waitForState = new Promise<void>((resolve) => {
      this.resolveState = resolve;
    });
  }

  async requestCall({ p, g, random }: DhConfig) {
    const pBN = readBigIntFromBuffer(Buffer.from(p), false);
    const randomBN = readBigIntFromBuffer(Buffer.from(random), false);

    const gA = modExp(BigInt(g), randomBN, pBN);

    this.gA = gA;
    this.p = pBN;
    this.random = randomBN;

    const gABytes = getByteArray(gA);
    const gAHash: Buffer = await sha256(gABytes);
    return Array.from(gAHash);
  }

  acceptCall({ p, g, random }: DhConfig) {
    const pLast = readBigIntFromBuffer(Buffer.from(p), false);
    const randomLast = readBigIntFromBuffer(Buffer.from(random), false);

    const gB = modExp(BigInt(g), randomLast, pLast);
    this.gB = gB;
    this.p = pLast;
    this.random = randomLast;

    return Array.from(getByteArray(gB));
  }

  async confirmCall(gAOrB: number[], emojiData: Uint16Array, emojiOffsets: number[]) {
    if (!this.random || !this.p) {
      throw new Error('Values not set');
    }

    if (this.isOutgoing) {
      this.gB = readBigIntFromBuffer(Buffer.from(gAOrB), false);
    } else {
      this.gA = readBigIntFromBuffer(Buffer.from(gAOrB), false);
    }
    const authKey = modExp(
      (!this.isOutgoing ? this.gA : this.gB)!,
      this.random,
      this.p,
    );
    const fingerprint: Buffer = await sha1(getByteArray(authKey));
    const keyFingerprint = readBigIntFromBuffer(fingerprint.slice(-8).reverse(), false);

    const emojis = await generateEmojiFingerprint(
      getByteArray(authKey),
      getByteArray(this.gA!),
      emojiData,
      emojiOffsets,
    );

    const key = new AuthKey();
    const rawBytes = getByteArray(authKey);
    await key.setKey(rawBytes);
    // Ensure auth key is exactly 256 bytes (pad with leading zeros if bigint has fewer digits)
    const authKeyBuf = Buffer.alloc(256);
    const raw = Buffer.from(rawBytes);
    raw.copy(authKeyBuf, 256 - raw.length);
    this.authKeyRaw = authKeyBuf;
    this.state = new MTProtoState(key, new Logger(), true, this.isOutgoing);
    this.resolveState!();

    const gAForConfirm = getByteArray(this.gA!);
    return {
      gA: Array.from(gAForConfirm),
      keyFingerprint: keyFingerprint.toString(),
      emojis,
      callKey: Array.from(authKeyBuf),
    };
  }

  async encode(data: string) {
    // tgcalls V2 signaling encryption (EncryptedConnection::prepareForSendingRawMessage)
    // Plaintext = [4B seq_BE][1B kCustomId=0x7F][4B payload_len_BE][payload]
    // Encrypted = [16B msgKey][AES-CTR(plaintext)]
    // x = 128 + (isOutgoing ? 0 : 8) for Signaling type
    if (!this.authKeyRaw) {
      await this.waitForState;
    }
    const authKey = this.authKeyRaw!;
    const payload = Buffer.from(data, 'utf-8');

    // V2 seq: counter | kSingleMessagePacketSeqBit | kMessageRequiresAckSeqBit
    const counter = ++this.seq;
    const kSingleMessagePacketSeqBit = 0x80000000;
    const kMessageRequiresAckSeqBit = 0x40000000;
    const seq = (counter | kSingleMessagePacketSeqBit | kMessageRequiresAckSeqBit) >>> 0;

    // Build V2 plaintext: [4B seq BE][1B 0x7F][4B len BE][payload]
    const plaintext = Buffer.alloc(4 + 1 + 4 + payload.length);
    plaintext.writeUInt32BE(seq, 0);
    plaintext[4] = 0x7F; // kCustomId
    plaintext.writeUInt32BE(payload.length, 5);
    payload.copy(plaintext, 9);

    // x = 128 for outgoing encode, 136 for incoming encode
    const x = 128 + (this.isOutgoing ? 0 : 8);

    const msgKeyLarge = await sha256(Buffer.concat([authKey.slice(88 + x, 88 + x + 32), plaintext]));
    const msgKey = msgKeyLarge.slice(8, 24);
    const { key, iv } = await callCryptoKeyIv(authKey, msgKey, x);
    const ctr = new CTR(key, iv);
    const encrypted = ctr.encrypt(plaintext);

    return Array.from(Buffer.concat([msgKey, encrypted]));
  }

  async decode(data: number[]): Promise<any> {
    const buf = Buffer.from(data);

    // tgcalls V2 decryption: [16B msgKey][AES-CTR(plaintext)]
    // Plaintext = [4B seq_BE][messages...] where messages are:
    //   kCustomId(0x7F): [1B 0x7F][4B len_BE][len bytes] = actual signaling JSON
    //   kEmptyId(0xFE): [1B 0xFE] = empty/service message
    //   kAckId(0xFF): [1B 0xFF] = ack
    // x = 128 + (isOutgoing ? 8 : 0) for Signaling type decode
    if (this.authKeyRaw && buf.length > 20) {
      const authKey = this.authKeyRaw;
      const msgKey = buf.slice(0, 16);
      const ciphertext = buf.slice(16);
      const x = 128 + (this.isOutgoing ? 8 : 0);

      try {
        const { key, iv } = await callCryptoKeyIv(authKey, msgKey, x);
        const ctr = new CTR(key, iv);
        const plaintext = ctr.decrypt(ciphertext);

        const verifyLarge = await sha256(Buffer.concat([authKey.slice(88 + x, 88 + x + 32), plaintext]));
        const verifyKey = verifyLarge.slice(8, 24);

        if (msgKey.equals(verifyKey)) {
          // Parse V2 packet: [4B seq][messages...]
          const packetSeq = plaintext.readUInt32BE(0);
          let offset = 4;

          // tgcalls V2: [4B seq][messages...] — iOS batches multiple messages
          // per packet (ack + Candidates + InitialSetup in same packet).
          // Must parse ALL kCustomId payloads, prioritize InitialSetup.
          const parsedMessages: any[] = [];

          while (offset < plaintext.length) {
            const type = plaintext[offset];
            if (type === 0xFE || type === 0xFF) {
              offset += 1;
              continue;
            }
            if (type === 0x7F) {
              offset += 1;
              if (offset + 4 > plaintext.length) break;
              const payloadLen = plaintext.readUInt32BE(offset);
              offset += 4;
              if (payloadLen > 100000 || offset + payloadLen > plaintext.length) break;
              const payload = plaintext.slice(offset, offset + payloadLen);
              offset += payloadLen;

              const jsonStr = payload.toString('utf-8');
              if (jsonStr.startsWith('{')) {
                try {
                  parsedMessages.push(JSON.parse(jsonStr));
                } catch (_e) { /* skip malformed */ }
              }
              continue;
            }
            // Unknown byte — scan forward for next 0x7F
            const nextCustom = plaintext.indexOf(0x7F, offset + 1);
            if (nextCustom !== -1 && nextCustom + 5 < plaintext.length) {
              offset = nextCustom;
              continue;
            }
            break;
          }

          if (parsedMessages.length > 0) {
            return parsedMessages.length === 1 ? parsedMessages[0] : parsedMessages;
          }

          // No kCustomId found — service-only packet (empty/ack)
          return undefined;
        }
        // V2 decrypt failed — likely stale session packet
        return undefined;
      } catch (e) {
        return undefined;
      }
    }

    // Fallback: plaintext format [4B len_le][json] (web-to-web only, no authKey)
    try {
      if (buf.length >= 4) {
        const len = buf.readUInt32LE(0);
        if (len > 0 && len <= buf.length - 4 && len < 100000) {
          const jsonStr = buf.slice(4, 4 + len).toString('utf-8');
          if (jsonStr.startsWith('{')) {
            return JSON.parse(jsonStr);
          }
        }
      }

      // Fallback: raw JSON (no prefix)
      const rawStr = buf.toString('utf-8');
      if (rawStr.startsWith('{')) {
        return JSON.parse(rawStr);
      }

      return undefined;
    } catch (e) {
      return undefined;
    }
  }
}

// https://github.com/TelegramV/App/blob/ead52320975362139cabad18cf8346f98c349a22/src/js/MTProto/Calls/Internal.js#L72
function computeEmojiIndex(bytes: Uint8Array) {
  return ((BigInt(bytes[0]) & 0x7Fn) << 56n)
    | ((BigInt(bytes[1]) << 48n))
    | ((BigInt(bytes[2]) << 40n))
    | ((BigInt(bytes[3]) << 32n))
    | ((BigInt(bytes[4]) << 24n))
    | ((BigInt(bytes[5]) << 16n))
    | ((BigInt(bytes[6]) << 8n))
    | ((BigInt(bytes[7])));
}

async function generateEmojiFingerprint(
  authKey: Uint8Array, gA: Uint8Array, emojiData: Uint16Array, emojiOffsets: number[],
) {
  const hash = await sha256(Buffer.concat([new Uint8Array(authKey), new Uint8Array(gA)]));
  const result = [];
  const emojiCount = emojiOffsets.length - 1;
  const kPartSize = 8;
  for (let partOffset = 0; partOffset !== hash.byteLength; partOffset += kPartSize) {
    const value = computeEmojiIndex(hash.subarray(partOffset, partOffset + kPartSize));
    const index = Number(value % BigInt(emojiCount));
    const offset = emojiOffsets[index];
    const size = emojiOffsets[index + 1] - offset;
    result.push(String.fromCharCode(...emojiData.subarray(offset, offset + size)));
  }
  return result.join('');
}

export function createPhoneCallState(params: ConstructorParameters<typeof PhoneCallState>) {
  currentPhoneCallState = new PhoneCallState(...params);
}

export function capturePhoneCallStateSnapshot(): PhoneCallStateSnapshot | undefined {
  if (!currentPhoneCallState) {
    return undefined;
  }

  const state = currentPhoneCallState as unknown as {
    isOutgoing: boolean;
    p?: bigint;
    random?: bigint;
    gA?: bigint;
    gB?: bigint;
  };

  return {
    isOutgoing: state.isOutgoing,
    ...(state.p !== undefined ? { pHex: state.p.toString(16) } : {}),
    ...(state.random !== undefined ? { randomHex: state.random.toString(16) } : {}),
    ...(state.gA !== undefined ? { gAHex: state.gA.toString(16) } : {}),
    ...(state.gB !== undefined ? { gBHex: state.gB.toString(16) } : {}),
  };
}

export function restorePhoneCallStateSnapshot(snapshot: PhoneCallStateSnapshot | undefined) {
  if (!snapshot) {
    currentPhoneCallState = undefined;
    return;
  }

  const restored = new PhoneCallState(snapshot.isOutgoing) as unknown as {
    p?: bigint;
    random?: bigint;
    gA?: bigint;
    gB?: bigint;
  };
  restored.p = snapshot.pHex !== undefined ? BigInt(`0x${snapshot.pHex}`) : undefined;
  restored.random = snapshot.randomHex !== undefined ? BigInt(`0x${snapshot.randomHex}`) : undefined;
  restored.gA = snapshot.gAHex !== undefined ? BigInt(`0x${snapshot.gAHex}`) : undefined;
  restored.gB = snapshot.gBHex !== undefined ? BigInt(`0x${snapshot.gBHex}`) : undefined;
  currentPhoneCallState = restored as unknown as PhoneCallState;
}

export function destroyPhoneCallState() {
  currentPhoneCallState = undefined;
}

type FunctionPropertyOf<T> = {
  [P in keyof T]: T[P] extends AnyFunction
    ? P
    : never
}[keyof T];

type ParamsOf<T extends FunctionPropertyOf<PhoneCallState>> = Parameters<PhoneCallState[T]>;
type ReturnTypeOf<T extends FunctionPropertyOf<PhoneCallState>> = ReturnType<PhoneCallState[T]>;

export function encodePhoneCallData(params: ParamsOf<'encode'>): ReturnTypeOf<'encode'> {
  if (!currentPhoneCallState) {
    // No state: cannot encrypt, send plaintext (web-to-web only)
    const [data] = params;
    const jsonBuf = Buffer.from(data, 'utf-8');
    const lenBuf = Buffer.alloc(4);
    lenBuf.writeUInt32LE(jsonBuf.length);
    return Promise.resolve(Array.from(Buffer.concat([lenBuf, jsonBuf])));
  }
  return currentPhoneCallState.encode(...params);
}

export async function decodePhoneCallData(params: ParamsOf<'decode'>) {
  if (!currentPhoneCallState) {
    // Fallback: decode plaintext even without PhoneCallState
    const [data] = params;
    const buf = Buffer.from(data);
    if (buf.length >= 4) {
      const len = buf.readUInt32LE(0);
      if (len > 0 && len <= buf.length - 4 && len < 100000) {
        const jsonStr = buf.slice(4, 4 + len).toString('utf-8');
        if (jsonStr.startsWith('{')) return JSON.parse(jsonStr);
      }
    }
    return undefined;
  }
  const result = await currentPhoneCallState.decode(...params);
  return result;
}

export function confirmPhoneCall(params: ParamsOf<'confirmCall'>): ReturnTypeOf<'confirmCall'> {
  return currentPhoneCallState!.confirmCall(...params);
}

export function getCallKey(): Uint8Array | undefined {
  if (!currentPhoneCallState) return undefined;
  // Access authKeyRaw via the exported getter
  return (currentPhoneCallState as any).authKeyRaw
    ? new Uint8Array((currentPhoneCallState as any).authKeyRaw)
    : undefined;
}

export function acceptPhoneCall(params: ParamsOf<'acceptCall'>): ReturnTypeOf<'acceptCall'> {
  return currentPhoneCallState!.acceptCall(...params);
}

export function requestPhoneCall(params: ParamsOf<'requestCall'>): ReturnTypeOf<'requestCall'> {
  return currentPhoneCallState!.requestCall(...params);
}
